/**
 * Security Page (/account/security): the signed-in staff user's two-step
 * verification (MFA / TOTP, tracker item 63). Set it up with an
 * authenticator app (QR code or the key typed in, then a code), see how many
 * recovery codes are left, get new ones, or turn it off (password and a code).
 *
 * Also the forced gate when the company requires MFA for the user's role:
 * ProtectedRoute keeps them here and the API client redirects here on a 403
 * MFA_ENROLLMENT_REQUIRED. So, like the change-password screen, it renders
 * outside AppLayout and offers sign-out.
 *
 * Confirming the setup returns a fresh session token (sessions from before
 * MFA was on stop working); it is applied immediately, while the recovery
 * codes are still on screen.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createAPIClient, IMfaApi } from '../api';
import { MfaEnrollmentStart, MfaReauth, MfaStatus } from '../api/mfaTypes';
import { getErrorMessage } from '../utils/apiError';
import { homePathFor } from '../utils/permissions';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../components/formStyles';
import { Toast } from '../components/Toast';
import { MfaReauthForm } from '../components/MfaReauthForm';

type Panel = 'none' | 'disable' | 'regenerate';

function RecoveryCodesBox({ codes }: { codes: string[] }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
      <p className="text-sm font-semibold text-amber-900">Save these recovery codes now</p>
      <p className="mt-1 text-sm text-amber-900">
        Each code signs you in once if you lose your phone. They are shown only this time; keep them somewhere safe (a password manager, or printed).
      </p>
      <ul aria-label="Recovery codes" className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-sm text-gray-900">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <button type="button" onClick={copy} className={`${secondaryButtonClass} mt-3`}>
        {copied ? 'Copied' : 'Copy codes'}
      </button>
    </div>
  );
}

export function SecurityPage() {
  const navigate = useNavigate();
  const { user, logout, markMfaEnrolled } = useAuth();
  const api = React.useMemo<IMfaApi>(() => createAPIClient(), []);
  const forced = user?.mfaEnrollmentRequired === true;

  const [status, setStatus] = useState<MfaStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Enrollment
  const [password, setPassword] = useState('');
  const [enrollment, setEnrollment] = useState<MfaEnrollmentStart | null>(null);
  const [code, setCode] = useState('');
  const [newCodes, setNewCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [panel, setPanel] = useState<Panel>('none');

  const load = useCallback(async () => {
    try {
      setLoadError(null);
      setStatus(await api.getMfaStatus());
    } catch (err) {
      setLoadError(getErrorMessage(err, 'Could not load your two-step verification settings'));
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setActionError(null);
    try {
      await work();
    } catch (err) {
      setActionError(getErrorMessage(err, 'That did not work'));
    } finally {
      setBusy(false);
    }
  };

  const startEnrollment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) {
      setActionError('Enter your password');
      return;
    }
    run(async () => {
      setEnrollment(await api.startMfaEnrollment(password));
      setPassword('');
    });
  };

  const confirmEnrollment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) {
      setActionError('Enter the 6-digit code from your app');
      return;
    }
    run(async () => {
      const result = await api.confirmMfaEnrollment(code.trim());
      // Sessions from before MFA was on no longer work: switch to the fresh token now
      markMfaEnrolled(result.token);
      setNewCodes(result.recoveryCodes);
      setEnrollment(null);
      setCode('');
      await load();
    });
  };

  const disable = (reauth: MfaReauth) =>
    run(async () => {
      await api.disableMfa(reauth);
      setPanel('none');
      setNewCodes(null);
      setNotice('Two-step verification is off.');
      await load();
    });

  const regenerate = (reauth: MfaReauth) =>
    run(async () => {
      setNewCodes(await api.regenerateMfaRecoveryCodes(reauth));
      setPanel('none');
      await load();
    });

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate('/login', { replace: true });
    }
  };

  const home = homePathFor(user);

  return (
    <div className="min-h-screen bg-gray-100 flex items-start sm:items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white rounded-lg shadow p-4 sm:p-6 space-y-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Two-step verification</h1>
          <p className="mt-1 text-sm text-gray-600">
            Sign in with your password and a code from an authenticator app on your phone (Google Authenticator, Microsoft Authenticator, 1Password and similar).
          </p>
          {user?.email && (
            <p className="mt-1 text-sm text-gray-600 break-words">
              Signed in as <strong>{user.email}</strong>
            </p>
          )}
        </div>

        {forced && !newCodes && (
          <p role="status" className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
            Your company requires two-step verification for your role. Set it up to continue.
          </p>
        )}
        {loadError && <Toast kind="error">{loadError}</Toast>}
        {notice && <Toast kind="success">{notice}</Toast>}

        {newCodes && <RecoveryCodesBox codes={newCodes} />}

        {status && !status.available && (
          <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            Two-step verification is not available on this server yet. Ask your administrator.
          </p>
        )}

        {status?.lockedUntil && (
          <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            Too many wrong codes. Try again after {new Date(status.lockedUntil).toLocaleTimeString()}.
          </p>
        )}

        {status?.available && !status.enabled && !newCodes && (
          <section aria-label="Set up two-step verification" className="space-y-3">
            {actionError && <Toast kind="error">{actionError}</Toast>}
            {!enrollment ? (
              <form onSubmit={startEnrollment} noValidate className="space-y-3">
                <p className="text-sm text-gray-700">Step 1 of 2: confirm your password.</p>
                <div>
                  <label htmlFor="mfa-setup-password" className={labelClass}>Your password</label>
                  <input
                    id="mfa-setup-password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={busy}
                    className={inputClass}
                  />
                </div>
                <button type="submit" disabled={busy} className={primaryButtonClass}>
                  {busy ? 'Working...' : 'Set up'}
                </button>
              </form>
            ) : (
              <form onSubmit={confirmEnrollment} noValidate className="space-y-3">
                <p className="text-sm text-gray-700">
                  Step 2 of 2: scan this code with your authenticator app, then enter the 6-digit code it shows.
                </p>
                <img src={enrollment.qrCodeDataUrl} alt="QR code for your authenticator app" className="h-48 w-48 border border-gray-200" />
                <div className="text-sm text-gray-700">
                  <p>Cannot scan it? Add an account by hand with this key:</p>
                  <p className="mt-1 break-all font-mono text-gray-900" data-testid="mfa-secret">
                    {enrollment.secret.replace(/(.{4})/g, '$1 ').trim()}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">Account: {enrollment.account} ({enrollment.issuer}). Time-based, 6 digits.</p>
                </div>
                <div>
                  <label htmlFor="mfa-setup-code" className={labelClass}>Code from the app</label>
                  <input
                    id="mfa-setup-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={7}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    disabled={busy}
                    className={inputClass}
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="submit" disabled={busy} className={primaryButtonClass}>
                    {busy ? 'Checking...' : 'Turn on'}
                  </button>
                  <button type="button" disabled={busy} onClick={() => setEnrollment(null)} className={secondaryButtonClass}>
                    Start again
                  </button>
                </div>
              </form>
            )}
          </section>
        )}

        {status?.enabled && (
          <section aria-label="Two-step verification is on" className="space-y-3">
            <p className="text-sm text-gray-800">
              <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">On</span>{' '}
              {status.enabledAt && <>since {new Date(status.enabledAt).toLocaleDateString()}. </>}
              {status.recoveryCodesRemaining} recovery code{status.recoveryCodesRemaining === 1 ? '' : 's'} left.
            </p>
            {status.required && <p className="text-sm text-gray-600">Your company requires it for your role.</p>}

            {panel === 'none' && (
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => { setPanel('regenerate'); setActionError(null); setNotice(null); }} className={secondaryButtonClass}>
                  New recovery codes
                </button>
                <button type="button" onClick={() => { setPanel('disable'); setActionError(null); setNotice(null); }} className={secondaryButtonClass}>
                  Turn off
                </button>
              </div>
            )}
            {panel === 'regenerate' && (
              <MfaReauthForm
                idPrefix="mfa-regenerate"
                needsFactor
                submitLabel="Create new codes"
                busy={busy}
                error={actionError}
                onSubmit={regenerate}
                onCancel={() => setPanel('none')}
                intro="Your current recovery codes will stop working."
              />
            )}
            {panel === 'disable' && (
              <MfaReauthForm
                idPrefix="mfa-disable"
                needsFactor
                submitLabel="Turn off two-step verification"
                busy={busy}
                error={actionError}
                onSubmit={disable}
                onCancel={() => setPanel('none')}
                danger
                intro={
                  status.required
                    ? 'Your company requires two-step verification: you will have to set it up again before you can continue.'
                    : 'Signing in will need only your password.'
                }
              />
            )}
          </section>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4">
          {forced ? (
            <button type="button" onClick={handleLogout} className={secondaryButtonClass}>
              Sign out
            </button>
          ) : (
            <Link to={home} className={`${secondaryButtonClass} inline-flex items-center`}>
              {newCodes ? 'I saved my codes, continue' : 'Back'}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
